import React, {useCallback, useEffect, useRef, useState} from "react";
import auth from "../../utils/AthnAuth";
import styles from "./ModaleScadenzaSessione.module.css";

/* WCAG 2.2.1 (Regolazione tempi di esecuzione, Livello A) — avviso di scadenza
   della sessione con possibilita' di prolungamento.

   Prima: allo scadere del timeout di inattivita' l'utente veniva disconnesso senza
   alcun preavviso e senza alcun modo di prolungare la sessione; i dati non salvati
   andavano persi. Il criterio 2.2.1 richiede che l'utente sia avvisato con almeno
   20 secondi di anticipo e possa estendere il limite con un'azione semplice.

   Ora: 2 minuti prima della scadenza compare questa finestra di dialogo, che offre
   "Continua a lavorare" (riarma il timer e rigenera il token) ed "Esci ora".

   Scelte di accessibilita':
   - role="alertdialog" + aria-modal="true": e' un avviso che richiede una risposta.
   - aria-labelledby / aria-describedby puntano al titolo e al testo reali.
   - Il focus si sposta sulla modale all'apertura e torna all'elemento di partenza
     alla chiusura (WCAG 2.4.3).
   - Focus trap: Tab e Shift+Tab restano dentro la modale finche' e' aperta.
   - Il conto alla rovescia che cambia ogni secondo e' in aria-live="off" e
     aria-hidden: un contatore annunciato ogni secondo sarebbe di per se' una
     barriera per gli screen reader. Un messaggio testuale equivalente viene
     aggiornato in aria-live="polite" solo ogni 30 secondi.
   - Esc PROLUNGA la sessione e chiude la modale. E' il comportamento meno
     pericoloso: Esc e' la chiusura istintiva di una finestra modale e non deve
     mai poter causare la perdita del lavoro non salvato. Chiudere senza prolungare
     lascerebbe l'utente convinto di avere ancora tempo mentre il logout continua
     ad avvicinarsi; uscire sarebbe apertamente distruttivo. */

const SELETTORI_FOCUSABILI = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function formattaResiduo(ms) {
    const totale = Math.max(0, Math.ceil(ms / 1000));
    const minuti = Math.floor(totale / 60);
    const secondi = totale % 60;
    if (minuti > 0) {
        return `${minuti} ${minuti === 1 ? "minuto" : "minuti"} e ${secondi} ${secondi === 1 ? "secondo" : "secondi"}`;
    }
    return `${secondi} ${secondi === 1 ? "secondo" : "secondi"}`;
}

function ModaleScadenzaSessione() {
    const [aperta, setAperta] = useState(false);
    const [residuo, setResiduo] = useState(0);
    // Testo annunciato dagli screen reader: aggiornato a intervalli discreti (30s),
    // non a ogni secondo.
    const [annuncio, setAnnuncio] = useState("");

    const dialogRef = useRef(null);
    const elementoPrecedenteRef = useRef(null);
    const scadenzaRef = useRef(0);
    const intervalloRef = useRef(null);
    const ultimoAnnuncioRef = useRef(null);

    const fermaConteggio = useCallback(() => {
        if (intervalloRef.current) {
            clearInterval(intervalloRef.current);
            intervalloRef.current = null;
        }
    }, []);

    // Chiusura con ripristino del focus al punto di partenza.
    const chiudi = useCallback(() => {
        fermaConteggio();
        setAperta(false);
        const precedente = elementoPrecedenteRef.current;
        elementoPrecedenteRef.current = null;
        if (precedente && typeof precedente.focus === "function" && document.contains(precedente)) {
            precedente.focus();
        }
    }, [fermaConteggio]);

    const prolunga = useCallback(() => {
        auth.prolungaSessione();
        chiudi();
    }, [chiudi]);

    const esciOra = useCallback(() => {
        fermaConteggio();
        setAperta(false);
        elementoPrecedenteRef.current = null;
        auth.logout();
    }, [fermaConteggio]);

    // Registrazione del callback di preavviso presso AthnAuth.
    useEffect(() => {
        const deregistra = auth.registraAvvisoScadenza((msResidui) => {
            // Difesa aggiuntiva: mai mostrare l'avviso a un utente non autenticato.
            if (!auth.sessioneAttiva()) return;
            elementoPrecedenteRef.current = document.activeElement;
            scadenzaRef.current = Date.now() + msResidui;
            setResiduo(msResidui);
            ultimoAnnuncioRef.current = null;
            setAnnuncio("");
            setAperta(true);
        });

        // Arma i timer anche quando l'utente arriva su una pagina con la sessione
        // gia' stabilita (page refresh): il _loginCallback non viene rieseguito.
        if (auth.sessioneAttiva()) {
            auth.avviaTimerInattivita();
        }

        return () => {
            deregistra();
        };
    }, []);

    // Conto alla rovescia: un solo intervallo, attivo solo a modale aperta.
    useEffect(() => {
        if (!aperta) {
            fermaConteggio();
            return undefined;
        }

        const aggiorna = () => {
            const rimanenti = scadenzaRef.current - Date.now();
            setResiduo(rimanenti > 0 ? rimanenti : 0);

            // Annuncio non invadente: solo ai passaggi di 30 secondi.
            const secondi = Math.max(0, Math.ceil(rimanenti / 1000));
            const scaglione = Math.ceil(secondi / 30);
            if (secondi > 0 && ultimoAnnuncioRef.current !== scaglione) {
                ultimoAnnuncioRef.current = scaglione;
                setAnnuncio(`Sessione in scadenza tra circa ${formattaResiduo(rimanenti)}.`);
            }

            if (rimanenti <= 0) {
                fermaConteggio();
            }
        };

        aggiorna();
        intervalloRef.current = setInterval(aggiorna, 1000);
        return () => fermaConteggio();
    }, [aperta, fermaConteggio]);

    // Focus iniziale sulla modale.
    useEffect(() => {
        if (aperta && dialogRef.current) {
            dialogRef.current.focus();
        }
    }, [aperta]);

    // Esc (prolunga) e focus trap.
    const onKeyDown = useCallback((event) => {
        if (event.key === "Escape") {
            event.preventDefault();
            prolunga();
            return;
        }
        if (event.key !== "Tab") return;

        const contenitore = dialogRef.current;
        if (!contenitore) return;
        const focusabili = Array.prototype.slice
            .call(contenitore.querySelectorAll(SELETTORI_FOCUSABILI))
            .filter((el) => el.offsetParent !== null || el === document.activeElement);
        if (focusabili.length === 0) {
            event.preventDefault();
            contenitore.focus();
            return;
        }
        const primo = focusabili[0];
        const ultimo = focusabili[focusabili.length - 1];
        const attivo = document.activeElement;

        if (event.shiftKey) {
            if (attivo === primo || attivo === contenitore || !contenitore.contains(attivo)) {
                event.preventDefault();
                ultimo.focus();
            }
        } else if (attivo === ultimo || !contenitore.contains(attivo)) {
            event.preventDefault();
            primo.focus();
        }
    }, [prolunga]);

    if (!aperta) return null;

    return (
        <div className={styles.overlay}>
            <div
                ref={dialogRef}
                className={styles.dialog}
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="scadenza-sessione-titolo"
                aria-describedby="scadenza-sessione-descrizione"
                tabIndex={-1}
                onKeyDown={onKeyDown}
            >
                <h2 id="scadenza-sessione-titolo" className={styles.titolo}>
                    La sessione sta per scadere
                </h2>
                <div id="scadenza-sessione-descrizione">
                    <p className={styles.testo}>
                        Per inattività la sessione scadrà a breve e verrà effettuata la
                        disconnessione automatica. I dati non salvati andranno persi.
                    </p>
                    <p className={styles.testo}>
                        Tempo rimanente:{" "}
                        {/* Il valore cambia ogni secondo: non deve essere annunciato di
                            continuo. L'informazione equivalente e' nella regione polite
                            sottostante, aggiornata ogni 30 secondi. */}
                        <span className={styles.contatore} aria-live="off" aria-hidden="true">
                            {formattaResiduo(residuo)}
                        </span>
                    </p>
                </div>
                {/* Annuncio discreto per screen reader. */}
                <p className="sr-only" aria-live="polite">{annuncio}</p>
                <div className={styles.comandi}>
                    <button
                        type="button"
                        id="scadenza-sessione-esci"
                        className={styles.bottoneSecondario}
                        onClick={esciOra}
                    >
                        Esci ora
                    </button>
                    <button
                        type="button"
                        id="scadenza-sessione-continua"
                        className={styles.bottonePrincipale}
                        onClick={prolunga}
                    >
                        Continua a lavorare
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ModaleScadenzaSessione;
